/**
 * SupplierPickerModal — aranabilir tedarikçi seçici modal.
 *
 * Düz Select tüm tedarikçileri DOM'a basar; büyük zincirlerde (yüzlerce
 * tedarikçi) patlar. Arama SERVER-SIDE'dır (debounced → /suppliers/?search=...):
 * yalnız eşleşen sayfa kadar satır basılır, infinite-scroll ile devamı çekilir.
 *
 * Tekli seçim için doğrudan kullanılır (onSelect). Çoklu seçim için
 * `excludeIds` ile zaten seçilenler gizlenir.
 */
import { useState } from "react";
import {
  Badge, Box, Button, Group, Loader, Modal, ScrollArea, Stack, Text, TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useDebouncedValue } from "@mantine/hooks";
import { useInfiniteResource } from "../../../shared/api";
import { useInfiniteScrollRef } from "../../../shared/ui";

interface Supplier {
  id: string;
  name: string;
  tax_id?: string;
  is_active: boolean;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Seçilen tedarikçi — modal kapanır. */
  onSelect: (supplier: Supplier) => void;
  /** Listeden gizlenecek tedarikçi id'leri (çoklu seçimde zaten seçilenler). */
  excludeIds?: string[];
  /** Verilirse YALNIZ bu id'ler gösterilir (RFQ'da kalemleri kapsayanlar).
   *  undefined ise tüm tedarikçiler (GR/PO/SupplierDetail vb. davranış). */
  allowIds?: string[];
}

export function SupplierPickerModal({
  opened, onClose, onSelect, excludeIds = [], allowIds,
}: Props) {
  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search.trim(), 300);

  const {
    items, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage,
  } = useInfiniteResource<Supplier>("suppliers", { search: debounced });

  const excluded = new Set(excludeIds);
  const allowed = allowIds ? new Set(allowIds) : null;
  const visible = items.filter(
    (s) => !excluded.has(s.id) && (allowed === null || allowed.has(s.id)),
  );

  const sentinelRef = useInfiniteScrollRef<HTMLDivElement>({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    itemCount: items.length,
  });

  return (
    <Modal opened={opened} onClose={onClose} title="Tedarikçi Seç" size="md">
      <Stack gap="sm">
        <Text size="xs" c="dimmed">İlişkilendirmek için tedarikçi arayın ve seçin</Text>
        <TextInput
          placeholder="Tedarikçi adı veya vergi no ile ara..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          autoFocus
        />

        <ScrollArea.Autosize mah={420} offsetScrollbars>
          {isLoading ? (
            <Group justify="center" py="xl"><Loader /></Group>
          ) : visible.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl" size="sm">
              {debounced ? "Eşleşen tedarikçi yok" : "Henüz tedarikçi yok"}
            </Text>
          ) : (
            <Stack gap={6}>
              {visible.map((s) => (
                <UnstyledButton
                  key={s.id}
                  onClick={() => { onSelect(s); onClose(); }}
                  style={{
                    border: "1px solid var(--mantine-color-gray-3)",
                    borderRadius: "var(--mantine-radius-md)",
                    padding: 10,
                  }}
                >
                  <Group gap="sm" wrap="nowrap" justify="space-between">
                    <Box style={{ minWidth: 0 }}>
                      <Text fw={600} size="sm" truncate>{s.name}</Text>
                      {s.tax_id && (
                        <Text size="xs" c="dimmed" truncate>VKN: {s.tax_id}</Text>
                      )}
                    </Box>
                    {!s.is_active && (
                      <Badge color="gray" variant="light" size="sm" style={{ flexShrink: 0 }}>
                        pasif
                      </Badge>
                    )}
                  </Group>
                </UnstyledButton>
              ))}
              {/* Infinite-scroll sentinel — görününce sonraki sayfa çekilir */}
              {hasNextPage && (
                <Box ref={sentinelRef}>
                  <Group justify="center" py="sm"><Loader size="xs" /></Group>
                </Box>
              )}
            </Stack>
          )}
        </ScrollArea.Autosize>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>İptal</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
